import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchParams } from '../../redux/searchParamsSlice';
import { searchEntityRecords, getRecord } from '../../utils/apiUtils';
import { convertToCSV, downloadCSV } from '../../utils/fileExportUtil';
import DataListView, { 
  VIEW_TYPE_SCREEN 
} from './DataListView';
import { getDisplayColumns, getAllColumns } from './columnUtils';
import { getFilters, getFiltersCriteria } from './filterUtils';
import { getTiles } from './tileUtils';
import { handleAPIError } from '../../utils/utils';
import { DEFAULT_PAGE_SIZE, SORT_DIRECTION_ASCENDNG, SORT_DIRECTION_DESCENDING } from '../dataTable/constants';
import './ListScreen.css';

export { VIEW_TYPE_SCREEN };

const normalizeColumn = (col, isCustom = false) => ({
  ...col,
  key: col.key || col.value,
  value: col.value || col.key,
  header: col.header || col.label,
  label: col.label || col.header,
  isCustom
});

const mergeColumns = (baseColumns, customColumns) => {
  let merged = baseColumns.map(c => normalizeColumn(c, false));
  if (!customColumns || customColumns.length === 0) return merged;
  const additions = [];
  customColumns.forEach(c => {
    const norm = normalizeColumn(c, true);
    const matchIndex = merged.findIndex(m => m.value === norm.value || m.key === norm.key);
    if (matchIndex > -1) {
      merged[matchIndex] = { ...merged[matchIndex], ...norm };
    } else {
      additions.push(norm);
    }
  });
  const sortedAdditions = additions.sort((a, b) => (a.order || 0) - (b.order || 0));
  sortedAdditions.forEach(customCol => {
    const insertionIndex = customCol.order !== undefined ? customCol.order : merged.length;
    const safeIndex = Math.max(0, Math.min(insertionIndex, merged.length));
    merged.splice(safeIndex, 0, customCol);
  });
  return merged;
};

const ListScreen = ({
  entityName,
  heading,
  subheading,
  modalHeading,
  showAddButton = true,
  enableExport = false,
  addScreenType = VIEW_TYPE_SCREEN,
  editScreenType = VIEW_TYPE_SCREEN,
  addScreen,
  editScreen,
  showSearch = false,
  rowActions = [],
  idColumn = 'id',
  defaultCriteria = null,
  addScreenAddtionalParams = {},
  editScreenAddtionalParams = {},
  onDrawerClose: onDrawerCloseCallback,
  columns = [],
  onRowClick = null,
  enableRowClick = false,
  onDelete: onDeleteProp,
  usageLimitConfig = null,
  customHeaderActions = null,
  emptyStateConfig = null,
  statusPollingConfig = null
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user.id);
  const searchParamsFromState = useSelector((state) => state.searchParams.searchParams[entityName]);

  const searchParams = useMemo(() => searchParamsFromState || {
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: '',
    sortDirection: SORT_DIRECTION_ASCENDNG,
    searchQuery: '',
    filterValues: {}
  }, [searchParamsFromState]);
  
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValues, setFilterValues] = useState({...searchParams.filterValues});
  const [searchQuery, setSearchQuery] = useState(searchParams.searchQuery);
  const [tileData, setTileData] = useState({});
  
  const isFilterApplied = useMemo(() => {
    return (searchParams.searchQuery && searchParams.searchQuery.length > 0) || 
           (searchParams.filterValues && Object.values(searchParams.filterValues).some(v => !!v));
  }, [searchParams]);

  const availableColumns = useMemo(() => {
    try {
      const standardAll = getAllColumns(entityName) || [];
      return mergeColumns(standardAll, columns);
    } catch {
      return columns.map(c => normalizeColumn(c, true));
    }
  }, [entityName, columns]);

  const getMergedDisplayColumns = () => {
    let standardDisplay = [];
    try {
      standardDisplay = getDisplayColumns(userId, entityName);
    } catch (error) {
      console.warn(`ListScreen: Could not load display columns for ${entityName}`, error);
      if (!columns || columns.length === 0) return [];
    }
    return mergeColumns(standardDisplay, columns);
  };

  const searchFilters = getFilters(entityName) ?? [];
  const entityTiles = getTiles(entityName) ?? [];

  const fetchData = async (searchParams1) => {
    const { filterValues, searchQuery, ...searchParamsForAPI} = searchParams1;
    setIsLoading(true);
    try {
      const searchCriteria = getFiltersCriteria(entityName, searchQuery, searchFilters, filterValues ?? {}, defaultCriteria)
      await searchEntityRecords(entityName, {...searchParamsForAPI, group: searchCriteria})
      .then(result => {
        setData(result.data?.list?.length ? result.data.list : []);
        setTotalRecords(result.data?.totalCount ?? 0);
        setTotalPages(Math.ceil(result.data.totalCount/searchParams1.pageSize));
      });
    }
    catch (error) {
      handleAPIError(error, '');
    }
    finally {
      setIsLoading(false);
    }
  }

  const fetchTileData = async (searchParams1) => {
    if (entityTiles.length > 0) {
      const { filterValues, searchQuery } = searchParams1;
      const searchCriteria = getFiltersCriteria(entityName, searchQuery, searchFilters, filterValues ?? {}, defaultCriteria);
      
      const tilePromises = entityTiles.map(tile => tile.dataFunction({ group: searchCriteria }));
      try {
        const results = await Promise.all(tilePromises);
        const newTileData = entityTiles.reduce((acc, tile, index) => {
          acc[tile.title] = results[index];
          return acc;
        }, {});
        setTileData(newTileData);
      } catch (error) {
        console.error("Error fetching tile data:", error);
        handleAPIError(error, 'Failed to load tile data');
      }
    }
  };

  const latestRef = useRef(null);
  latestRef.current = { searchParams, fetchData, fetchTileData };

  const filterValuesKey = JSON.stringify(searchParams.filterValues);

  useEffect(() => {
    latestRef.current.fetchData(searchParams)
  }, [searchParams]);

  useEffect(() => {
    const { searchParams: latestSearchParams, fetchTileData: latestFetchTileData } = latestRef.current;
    const searchParamsForTiles = {
      filterValues: latestSearchParams.filterValues,
      searchQuery: latestSearchParams.searchQuery,
    };
    latestFetchTileData(searchParamsForTiles);
  }, [filterValuesKey, searchParams.searchQuery]);

  const onRecordSaved = (entityName) => {
    const newSearchParams = {...searchParams, pageNumber: 1};
    dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
  };

  const onSort = (field) => {
    let newSortDirection = searchParams.sortDirection;
    
    if(searchParams.orderBy === field) {
      newSortDirection = searchParams.sortDirection === SORT_DIRECTION_ASCENDNG ? SORT_DIRECTION_DESCENDING : SORT_DIRECTION_ASCENDNG;
    } else {
      newSortDirection = SORT_DIRECTION_ASCENDNG;
    }
    
    const newSearchParams = {...searchParams, orderBy: field, sortDirection: newSortDirection, pageNumber: 1};
    dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
  };

  const onPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      const newSearchParams = {...searchParams, pageNumber: newPage};
      dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
    }
  };

  const onPageSizeChange = (newSize) => {
    const newSearchParams = {...searchParams, filterValues, searchQuery, pageSize: newSize, pageNumber: 1};
    dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
  };

  const onFilterChange = (newValues) => {
    setFilterValues(newValues);
  };

  const onSearchQueryChange = (value) => {
    setSearchQuery(value);
  };

  const onSearch = () => {
    const newSearchParams = {...searchParams, filterValues, searchQuery, pageNumber: 1};
    dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
  }

  const handleExport = async () => {
    try {
      const { filterValues, searchQuery, orderBy, sortDirection } = searchParams;
      const searchCriteria = getFiltersCriteria(entityName, searchQuery, searchFilters, filterValues ?? {}, defaultCriteria);
      
      const exportParams = {
        orderBy,
        sortDirection,
        group: searchCriteria,
        pageNumber: 1,
        pageSize: -1,
      };

      const result = await searchEntityRecords(entityName, exportParams);
      const recordsToExport = result.data?.list ?? [];

      if (recordsToExport.length === 0) {
        alert('No data available to export.');
        return;
      }
      
      let exportColumns = getMergedDisplayColumns();
      if (exportColumns.length === 0) {
          console.warn('Export: falling back to raw data, no columns definition found.');
      }

      const csvData = convertToCSV(recordsToExport, exportColumns);
      downloadCSV(csvData, entityName);

    } catch (error) {
      handleAPIError(error, 'Failed to export data');
    }
  };

  const onDelete = async (row) => {
    if (onDeleteProp) {
      try {
        await onDeleteProp(row);
      } catch (error) {
        console.error("Delete operation failed", error);
        return; 
      }
    }

    const displayedRowsBeforeDeletion = data.length;
    const hasRowId = data.some((item) => item.id === row.id)
    
    setData(prevData => prevData.filter((item) => item.id !== row.id));
    
    if(hasRowId) {
      setTotalRecords(prevTotalRecords => prevTotalRecords - 1);
      if(displayedRowsBeforeDeletion === 1 && totalRecords > displayedRowsBeforeDeletion) {
        setTotalPages(prevTotalPages => prevTotalPages - 1);
        const newSearchParams = {...searchParams, pageNumber: searchParams.pageNumber - 1};
        dispatch(setSearchParams({entityName, searchParams: newSearchParams}))
      }
      else if(displayedRowsBeforeDeletion > 1 && searchParams.pageNumber < totalPages) {
        fetchData(searchParams)
      }
    }
  }

  const onUpdateRow = (updatedRow) => {
    setData(prevData => prevData.map(row => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)));
  };

  const pollingIntervalRef = useRef(null);

  const pollPendingRecords = useCallback(async () => {
    if (!statusPollingConfig) return;
    const { field, pendingValue } = statusPollingConfig;
    const pendingRecords = data.filter(row => row[field] === pendingValue);
    if (pendingRecords.length === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }
    
    for (const record of pendingRecords) {
      try {
        const updatedRecord = await getRecord(entityName, record.id);
        if (updatedRecord && updatedRecord[field] !== pendingValue) {
          onUpdateRow(updatedRecord);
        }
      } catch (err) {
        console.error('Polling error for record', record.id, err);
      }
    }
  }, [data, statusPollingConfig, entityName]);

  useEffect(() => {
    if (!statusPollingConfig) return;
    const { field, pendingValue, interval = 5000 } = statusPollingConfig;
    const hasPending = data.some(row => row[field] === pendingValue);
    if (hasPending && !pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(pollPendingRecords, interval);
    } else if (!hasPending && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [data, statusPollingConfig, pollPendingRecords]);

  return (
    <DataListView
      entityName={entityName}
      heading={heading}
      subheading={subheading}
      modalHeading={modalHeading}
      idColumn={idColumn}
      data={data}
      totalRecords={totalRecords}
      currentPage={searchParams.pageNumber}
      pageSize={searchParams.pageSize}
      orderBy={searchParams.orderBy}
      sortDirection={searchParams.sortDirection}
      isLoading={isLoading}
      searchQuery={searchQuery}
      filterValues={filterValues}
      searchFilters={searchFilters}
      isFilterApplied={isFilterApplied}
      tiles={entityTiles}
      tileData={tileData}
      showAddButton={showAddButton}
      enableExport={enableExport}
      showSearch={showSearch}
      addScreenType={addScreenType}
      editScreenType={editScreenType}
      addScreen={addScreen}
      editScreen={editScreen}
      addScreenAddtionalParams={addScreenAddtionalParams}
      editScreenAddtionalParams={editScreenAddtionalParams}
      onDrawerClose={onDrawerCloseCallback}
      displayColumns={getMergedDisplayColumns()}
      availableColumns={availableColumns}
      customColumns={columns}
      rowActions={rowActions}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSort={onSort}
      onSearch={onSearch}
      onSearchQueryChange={onSearchQueryChange}
      onFilterChange={onFilterChange}
      onDelete={onDelete}
      onUpdateRow={onUpdateRow}
      onRecordSaved={onRecordSaved}
      onExport={handleExport}
      onRowClick={onRowClick}
      enableRowClick={enableRowClick}
      usageLimitConfig={usageLimitConfig}
      customHeaderActions={customHeaderActions}
      emptyStateConfig={emptyStateConfig}
    />
  );
};

export default ListScreen;